import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from './useGlynt';
import { clearCachedCertification } from '../../utils/certificationCache';
import { IRQCallbacks } from '../../types/my-types';

type IGroundTruthUpdateMutationParams = {
  trainingSetId: string;
  document: string;
  mutationData: {
    id: string;
  };
};

export const useGroundTruthDelete = (optimisticUpdate: boolean, callbacks?: IRQCallbacks) => {
  const glynt = useGlynt();
  const cache = useQueryClient();

  return useMutation({
    mutationFn: // FIXME: TYPE data
    async (data: IGroundTruthUpdateMutationParams) => {
      return await (await glynt).groundTruths.del(data.mutationData.id);
    },

    ...callbacks,

    onSettled: async (data, error, variables) => {
      // Only on success: a failed edit revoked nothing server-side.
      if (!error) {
        await clearCachedCertification(cache, variables.trainingSetId);
      }

      // Invalidate mark validation for this document
      cache.invalidateQueries({
        queryKey: [
          'mark-validation',
          variables.trainingSetId,
          variables.document,
        ]
      });

      if (!optimisticUpdate) {
        cache.invalidateQueries({
          queryKey: ['groundTruths']
        });
      }

      // Call the original onSettled if provided
      callbacks?.onSettled?.();
    },

    onMutate: async (data) => {
      if (optimisticUpdate) {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await cache.cancelQueries({
          queryKey: ['groundTruths', data.document, data.trainingSetId]
        });

        // // Snapshot the previous value
        const previousGroundTruths: any = cache.getQueryData([
          'groundTruths',
          data.document,
          data.trainingSetId,
        ]);

        // Grab the previous pages and results
        const previousPages = previousGroundTruths['pages'] || {};

        // Find and remove the ground truth that is being edited
        const newPages = previousPages.map((page: any) => {
          const newResults = page.results.filter((result: any) => {
            return result.id !== data.mutationData.id;
          });

          return {
            ...page,
            results: newResults,
          };
        });

        // Update ground truths object with updated results
        const updatedGroundTruths = {
          ...previousGroundTruths,
          pages: newPages,
        };

        // Update cache with updated ground truths
        cache.setQueryData(
          ['groundTruths', data.document, data.trainingSetId],
          updatedGroundTruths
        );

        // Return a context object with the snapshotted value
        return { previousGroundTruths };
      }
    }
  });
};
